/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { cn } from '@repo/shared-client/lib/utils'
import type { PointerEvent } from 'react'
import type { CropHandle, CropRect, ImageRect } from './image-editor-types'
import { CROP_HANDLE_SIZE_PX } from './image-editor-types'

const CROP_HANDLES: Array<{ handle: CropHandle; className: string }> = [
  { handle: 'nw', className: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize' },
  { handle: 'ne', className: 'top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize' },
  { handle: 'sw', className: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize' },
  { handle: 'se', className: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize' },
]

type ImageEditorCropFrameProps = {
  crop: CropRect
  imageRect: ImageRect
  moveLabel: string
  resizeLabel: string
  onMovePointerDown: (event: PointerEvent<HTMLDivElement>) => void
  onResizePointerDown: (handle: CropHandle, event: PointerEvent<HTMLButtonElement>) => void
}

const ImageEditorCropFrame = ({
  crop,
  imageRect,
  moveLabel,
  resizeLabel,
  onMovePointerDown,
  onResizePointerDown,
}: ImageEditorCropFrameProps) => (
  <div
    aria-label={moveLabel}
    className="absolute box-border border-2 border-white"
    onPointerDown={onMovePointerDown}
    role="presentation"
    style={{
      left: crop.x * imageRect.width,
      top: crop.y * imageRect.height,
      width: crop.width * imageRect.width,
      height: crop.height * imageRect.height,
      boxShadow: '0 0 0 9999px rgb(0 0 0 / 55%)',
    }}
  >
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute inset-y-0 left-1/3 w-px bg-white/35" />
      <div className="absolute inset-y-0 left-2/3 w-px bg-white/35" />
      <div className="absolute inset-x-0 top-1/3 h-px bg-white/35" />
      <div className="absolute inset-x-0 top-2/3 h-px bg-white/35" />
    </div>
    {CROP_HANDLES.map(({ handle, className }) => (
      <button
        aria-label={resizeLabel}
        className={cn('absolute z-30 rounded-full bg-white shadow-md ring-2 ring-black/40', className)}
        key={handle}
        onPointerDown={(event) => onResizePointerDown(handle, event)}
        style={{ width: CROP_HANDLE_SIZE_PX, height: CROP_HANDLE_SIZE_PX }}
        type="button"
      />
    ))}
  </div>
)

export default ImageEditorCropFrame
